import {
    Alert,
    Box,
    CircularProgress,
    Skeleton,
    Stack,
    styled,
    useTheme
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { RadioButton } from '@/components/shared/RadioButton';
import {
    Caption,
    SMRegular,
    TextLGSemiBold,
    TextMDSemiBold
} from '@/typography/Typography';
import { HouseAccountAiiaCandidateAccount } from '@/services/PaymentsServiceV2';
import { useFormatting } from '@/utils/formatting';

const PickerContainer = styled(Box)(({ theme }) => ({
    maxWidth: '560px',
    margin: '0 auto',
    padding: theme.spacing(3),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.greyBorder}`,
    boxShadow: '0px 1px 2px 0px #1018280D',
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left'
}));

interface AccountCardProps {
    selected?: boolean;
    busy?: boolean;
}

const AccountCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'busy'
})<AccountCardProps>(({ theme, selected, busy }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: '8px',
    cursor: busy ? 'default' : 'pointer',
    backgroundColor: selected
        ? theme.palette.selectedRowColor
        : theme.palette.background.paper,
    border: `1px solid ${
        selected ? theme.palette.primary.main : theme.palette.greyBorder
    }`
}));

const ButtonRow = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3)
}));

interface HouseAccountAiiaAccountPickerProps {
    accounts: HouseAccountAiiaCandidateAccount[];
    isConfirming: boolean;
    error?: string;
    onConfirm: (accountId: string) => void;
    onCancel: () => void;
}

/**
 * Step two of the house-account Aiia connect flow, shown only when the consent
 * granted several bank accounts and the backend parked the exchange instead of
 * binding one. Nothing is bound until the operator confirms; abandoning the page
 * is safe because the parked selection expires server-side on its own.
 */
export const HouseAccountAiiaAccountPicker = ({
    accounts,
    isConfirming,
    error,
    onConfirm,
    onCancel
}: HouseAccountAiiaAccountPickerProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { getBalanceLabel } = useFormatting();
    const [selectedAccountId, setSelectedAccountId] = useState<string>();

    // Intl throws for style: 'currency' without a currency code, and Aiia may report an account
    // without one. A missing balance means "unknown", not zero — showing 0 would falsely claim
    // the account is empty, so the amount is dropped rather than guessed. The currency still
    // stands on its own: a consent can span currencies, which is often what tells two otherwise
    // similar accounts apart.
    const amountLabel = (account: HouseAccountAiiaCandidateAccount) => {
        if (!account.currency) {
            return undefined;
        }
        if (account.availableBalance == null) {
            return `${t('transaction_monitoring.select_account_currency')}: ${
                account.currency
            }`;
        }
        return `${t(
            'transaction_monitoring.select_account_available_balance'
        )}: ${getBalanceLabel(account.availableBalance, account.currency)}`;
    };

    return (
        <PickerContainer data-testid="aiia-account-picker">
            <TextLGSemiBold>
                {t('transaction_monitoring.select_account_title')}
            </TextLGSemiBold>
            <SMRegular
                color={theme.palette.text.secondary}
                marginTop={0.5}
                marginBottom={2}
            >
                {t('transaction_monitoring.select_account_description')}
            </SMRegular>

            {error && (
                <Alert severity="error" sx={{ marginBottom: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack gap={1} role="radiogroup" aria-busy={isConfirming}>
                {accounts.map((account) => {
                    const isSelected = account.id === selectedAccountId;
                    const select = () => {
                        if (!isConfirming) {
                            setSelectedAccountId(account.id);
                        }
                    };
                    const amount = amountLabel(account);

                    return (
                        <AccountCard
                            key={account.id}
                            selected={isSelected}
                            busy={isConfirming}
                            onClick={select}
                        >
                            <Stack direction="column" gap={0.25}>
                                <TextMDSemiBold>
                                    {account.providerName ??
                                        t(
                                            'transaction_monitoring.select_account_unknown_bank'
                                        )}
                                </TextMDSemiBold>
                                <SMRegular color={theme.palette.text.secondary}>
                                    {account.name}
                                </SMRegular>
                                {account.iban && (
                                    <Caption>
                                        {`${t(
                                            'transaction_monitoring.select_account_iban'
                                        )}: ${account.iban}`}
                                    </Caption>
                                )}
                                {amount && <Caption>{amount}</Caption>}
                            </Stack>
                            <RadioButton
                                checked={isSelected}
                                disabled={isConfirming}
                                onChange={select}
                                value={account.id}
                                inputProps={{ 'aria-label': account.name }}
                            />
                        </AccountCard>
                    );
                })}
            </Stack>

            <ButtonRow>
                <SecondaryButtonRegular
                    onClick={onCancel}
                    disabled={isConfirming}
                >
                    {t('transaction_monitoring.select_account_cancel')}
                </SecondaryButtonRegular>
                <PrimaryButton
                    onClick={() =>
                        selectedAccountId && onConfirm(selectedAccountId)
                    }
                    disabled={!selectedAccountId || isConfirming}
                >
                    {isConfirming ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        t('transaction_monitoring.select_account_confirm')
                    )}
                </PrimaryButton>
            </ButtonRow>
        </PickerContainer>
    );
};

/**
 * Placeholder for the in-flight exchange. The operator lands on the callback
 * page before the result is known, and the outcome decides whether a picker
 * appears at all, so the shell is drawn ahead of it.
 */
export const HouseAccountAiiaAccountPickerSkeleton = () => (
    <PickerContainer data-testid="aiia-account-picker-loading">
        <Skeleton variant="text" width={280} height={28} />
        <Skeleton variant="text" width={420} height={20} />
        <Stack gap={1} marginTop={2}>
            <Skeleton variant="rounded" height={88} />
            <Skeleton variant="rounded" height={88} />
        </Stack>
    </PickerContainer>
);

export default HouseAccountAiiaAccountPicker;
